import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import type { ApiResponse } from './types';
import type {
  HelpArticleResponse,
  HelpArticleVote,
  HelpAssistantAskPayload,
  HelpAssistantAskResponse,
  HelpAssistantFeedbackPayload,
  HelpCategoryResponse,
  HelpCenterTasksData,
  HelpCenterTopicsData,
  HelpEventPayload,
  HelpSearchResponse,
} from '@/layout/AppHelpCenter/types';

export type GetHelpCenterTasksResponse = ApiResponse<HelpCenterTasksData>;
export type GetHelpCenterArticleListResponse =
  ApiResponse<HelpCategoryResponse>;
export type GetHelpCenterTopicsResponse = ApiResponse<HelpCenterTopicsData>;
export type HelpCenterSearchResponse = ApiResponse<HelpSearchResponse>;
export type GetHelpCenterArticleResponse = ApiResponse<HelpArticleResponse>;

export const helpCenterSearch = (
  token: string | null,
  model: { search?: string }
): Promise<HelpCenterSearchResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.help_center}search/`,
        model,
        token ?? undefined
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error while Help Center searching...', error?.response);
        reject(error?.response);
      });
  });
};

// params: "page=shipments", or "page=all&current=shipments" for See all.
export const GetHelpCenterTasks = (
  token: string | null,
  params: string
): Promise<GetHelpCenterTasksResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.help_center}tasks/?${params}`,
        token ?? undefined,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(
          'Error while fetching Help Center tasks',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const GetHelpCenterTopics = (
  token: string | null
): Promise<GetHelpCenterTopicsResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.help_center}topics/`,
        token ?? undefined,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(
          'Error while fetching Help Center topics',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const GetHelpCenterArticleList = (
  token: string | null,
  topicId: string
): Promise<GetHelpCenterArticleListResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.help_center}topics/${topicId}/articles/`,
        token ?? undefined,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(
          'Error while fetching Help Center article list',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const GetHelpCenterArticle = (
  token: string | null,
  articleId: string
): Promise<GetHelpCenterArticleResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.help_center}articles/${articleId}/`,
        token ?? undefined,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(
          'Error while fetching Help Center article',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const PostHelpArticleFeedback = (
  token: string | null,
  articleId: string,
  model: { value: HelpArticleVote }
): Promise<ApiResponse<unknown>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.help_center}articles/${articleId}/feedback/`,
        model,
        token ?? undefined
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(
          'Error while sending Help Center article feedback',
          error?.response
        );
        reject(error?.response);
      });
  });
};

export const AskHelpAssistant = (
  token: string | null,
  model: HelpAssistantAskPayload
): Promise<ApiResponse<HelpAssistantAskResponse>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.help_center}assistant/ask/`,
        model,
        token ?? undefined
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error while asking the Help assistant', error?.response);
        reject(error?.response);
      });
  });
};

export const PostHelpAssistantFeedback = (
  token: string | null,
  model: HelpAssistantFeedbackPayload
): Promise<ApiResponse<unknown>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.help_center}assistant/feedback/`,
        model,
        token ?? undefined
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error(
          'Error while sending Help assistant feedback',
          error?.response
        );
        reject(error?.response);
      });
  });
};

// UI analytics (PRD telemetry). Callers fire and forget; see useHelpEvent.
export const PostHelpCenterEvent = (
  token: string | null,
  model: HelpEventPayload
): Promise<ApiResponse<unknown>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.help_center}events/`,
        model,
        token ?? undefined
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};
